import { motion } from "framer-motion";
import {
  AlertCircle,
  ClipboardList,
  SquareArrowOutUpRight,
} from "lucide-react";
import { useEffect, useState, useRef, useCallback, useContext, Fragment } from "react";
import toast from "react-hot-toast";
import { useDashboard } from "../Dashboard/Hooks/useDashboard";
import { getStatusIconAndColor, TASK_STATUS_LABELS } from "../Sites/Constants";
import { formatAmountINR } from "../Dashboard/Constants";
import { formatTimeAMPM } from "../../Utils/timeUtils";
import { getFormatDate, toProperCase } from "../../Utils";
import { useNavigate } from "react-router-dom";
import { VIEW_BILL, VIEW_DETAILED_PAGE } from "../../routes";
import { UserContext } from "../../Context/UserContext";
import { ROLES } from "../../Constants/Strings";

const TABS = [
  { key: "bills", label: "Bills" },
  { key: "attendance", label: "Attendance" },
];

const LIMIT = 5; // Number of items per page

const Approvals = () => {
  const [approvalDetails, setApprovalDetails] = useState({
    bills: { results: [], offset: 0, hasMore: true },
    attendance: { results: [], offset: 0, hasMore: true },
  });
  const [activeTab, setActiveTab] = useState("bills");
  const [loading, setLoading] = useState(false);
  const { fetchApprovals } = useDashboard();
  const observerRef = useRef();
  const navigate = useNavigate();
  const { user } = useContext(UserContext);

  const isRestrictedRole = [
    ROLES.MANAGING_DIRECTOR,
    ROLES.HEAD_OFFICE,
    ROLES.GOVERNMENT_OFFICER,
  ].includes(user.role);

  const fetchData = async (customOffset = null) => {
    if (loading || !approvalDetails[activeTab].hasMore) return;
    setLoading(true);
    try {
      const offset = customOffset ?? approvalDetails[activeTab].offset;
      const res = await fetchApprovals({
        type: activeTab,
        offset,
        limit: LIMIT,
      });
      if (res) {
        setApprovalDetails((prev) => ({
          ...prev,
          [activeTab]: {
            results:
              offset === 0
                ? res.results
                : [...prev[activeTab]?.results, ...res?.results],
            offset: offset + res?.results?.length,
            hasMore: res?.results?.length === LIMIT,
          },
        }));
      }
    } catch (err) {
      toast.error("Failed to load approvals");
    } finally {
      setLoading(false);
    }
  };

  // When tab changes → reset & fetch fresh
  useEffect(() => {
    if (!isRestrictedRole) fetchData(0);
  }, [activeTab]);

  const lastItemRef = useCallback(
    (node) => {
      if (loading) return;
      if (observerRef.current) observerRef.current.disconnect();

      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && approvalDetails[activeTab].hasMore) {
          fetchData();
        }
      });

      if (node) observerRef.current.observe(node);
    },
    [loading, approvalDetails, activeTab]
  );

  const renderBills = () =>
    approvalDetails.bills.results.length > 0 ? (
      approvalDetails.bills.results.map((bill, idx) => {
        const { icon, color } = getStatusIconAndColor(bill.status);
        const isLastItem = idx === approvalDetails?.bills?.results?.length - 1;
        return (
          <li
            key={bill.id}
            ref={isLastItem ? lastItemRef : null}
            className="p-2 rounded-lg border text-sm flex items-center justify-between shadow-sm"
          >
            <div className="max-w-[50%]">
              <div className="font-medium mb-2">
                {formatAmountINR(bill?.amount)}
              </div>
              <div className="flex flex-col">
                <div className="text-gray-600 text-xs truncate max-w-full">
                  Site: {bill.site}
                </div>
                <div className="text-gray-600 text-xs">
                  Category: {toProperCase(bill.category)}
                </div>
                <div className="text-gray-600 text-xs">
                  Date: {getFormatDate(bill.date)}
                </div>
              </div>
            </div>
            <div className="flex flex-col justify-end items-end gap-4 md:gap-8 md:mt-1.5">
              <SquareArrowOutUpRight
                size={14}
                onClick={() => navigate(VIEW_BILL.replace(":billId", bill.id))}
                className="text-blue-600 cursor-pointer hover:text-blue-800 transition-colors duration-200"
              />
              <div
                className={`flex items-center ${color} px-1.5 py-1 rounded-md gap-2 text-xs mt-4 md:mt-0`}
              >
                {icon} {TASK_STATUS_LABELS[bill?.status]}
              </div>
            </div>
          </li>
        );
      })
    ) : (
      <div className="w-full  h-[280px] flex justify-center items-center text-gray-300 flex-col">
        <ClipboardList size={35} />
        <span>No Bills</span>
      </div>
    );

  const renderAttendance = () =>
    approvalDetails.attendance?.results?.length > 0 ? (
      approvalDetails.attendance?.results?.map((att, idx) => {
        const { icon, color } = getStatusIconAndColor(att.status);
        const isLastItem =
          idx === approvalDetails?.attendance?.results?.length - 1;
        return (
          <li
            key={att.id}
            ref={isLastItem ? lastItemRef : null}
            className="p-2 rounded-lg border text-sm flex justify-between shadow-sm"
          >
            <div className="max-w-[50%] md:max-w-[60%] flex flex-col justify-end items-start gap-2">
              <div className="font-medium mb-2 md:mb-0 truncate max-w-full">
                {att.site}
              </div>
              <div className="flex flex-col md:flex-row flex-wrap md:gap-2 text-xs">
                <div>{getFormatDate(att.date)}</div>
                <div className="flex">
                  {att?.events?.map((event, index) => (
                    <>
                      <div
                        key={event.id}
                        className="flex items-center text-green-700"
                      >
                        {formatTimeAMPM(event?.time).toUpperCase()}{" "}
                      </div>
                      {att?.events.length === 2 && index === 0 ? (
                        <div className="px-1"> - </div>
                      ) : (
                        ""
                      )}
                    </>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex flex-col justify-end items-end gap-4 md:gap-2">
              <SquareArrowOutUpRight
                size={14}
                onClick={() =>
                  navigate(VIEW_DETAILED_PAGE.replace(":id", att.id))
                }
                className="text-blue-600 cursor-pointer hover:text-blue-800 transition-colors duration-200"
              />
              <div
                className={`flex items-center ${color} px-1.5 py-1 rounded-md gap-2 mt-4 md:mt-0 text-xs h-max`}
              >
                {icon} {TASK_STATUS_LABELS[att.status]}
              </div>
            </div>
          </li>
        );
      })
    ) : (
      <div className="w-full  h-[280px] flex justify-center items-center text-gray-300 flex-col">
        <ClipboardList size={35} />
        <span>No attendance</span>
      </div>
    );

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      className="lg:col-span-2"
    >
      <div className="rounded-2xl shadow-md bg-white px-4 pt-2 pb-3">
        <div className="flex justify-between mb-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />{" "}
            {user.role === ROLES.SITE_ENGINEER
              ? "My Approval"
              : "Approvals Actions"}
          </h2>

          <div className="flex justify-end gap-3">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-3 py-2 text-sm font-medium transition-colors duration-200 whitespace-nowrap ${
                  activeTab === tab.key
                    ? "border-b-2 border-blue-500 text-blue-600"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="h-[275px] overflow-y-auto">
          {[
            ROLES.MANAGING_DIRECTOR,
            ROLES.HEAD_OFFICE,
            ROLES.GOVERNMENT_OFFICER,
          ].includes(user.role) ? (
            <div className="w-full  h-[280px] flex justify-center items-center text-gray-300 flex-col">
              <ClipboardList size={35} />
              <span>No Approval Actions yet</span>
            </div>
          ) : (
            <ul className="space-y-3 relative">
              {activeTab === "bills" ? renderBills() : renderAttendance()}
              {loading && (
                <p className="text-gray-500 text-center absolute top-0 left-0 h-full w-full">
                  Loading more...
                </p>
              )}
            </ul>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default Approvals;
